import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  FormLabel
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import * as yup from 'yup';
import ReactQuill from 'react-quill'; // Import Quill
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import { toast } from 'react-toastify';
import { apipost } from '../../service/api';

const stripHtml = (s = '') => s.replace(/<[^>]*>/g, '').trim();

const validationSchema = yup.object({
  title: yup.string().required('Title is required'),
  body: yup
    .string()
    .test('not-empty-html', 'Body is required', (v) => stripHtml(v || '').length > 0),
  categories: yup.array().min(1, 'At least one category is required'),
  callToActionTitle: yup.string().required('Call to action title is required'),
  callToActionButtonText: yup.string().required('Button text is required'),
  callToActionLink: yup.string().required('Call to action link is required'),
  previewBlurb: yup
  .string()
  .required('Preview blurb is required')
  .max(300, 'Preview must be 300 characters or less'),
});

const CLOUD_UPLOAD_ENDPOINT = 'cloudinary/upload/image'; 
// 
// and returns { url: 'https://res.cloudinary.com/.../image.jpg' } (or { secure_url: ... })



const AddBlogPost = ({ open, handleClose, setUserAction }) => {
  const [image, setImage] = useState(null);
  const [editorValue, setEditorValue] = useState('');
  const userid = localStorage.getItem('user_id');
  const [uploading, setUploading] = useState(false);
  const quillRef = useRef(null);


   // --- Upload helper: calls your backend, expects { url } in response
  const uploadViaBackend = async (file) => {
    const fd = new FormData();
    fd.append('headshot', file);
    // if you need folder/context etc., append them here too
    // fd.append('folder', 'blog');

    const res = await apipost(CLOUD_UPLOAD_ENDPOINT, fd, true);
    if (!res || res.status < 200 || res.status >= 300) {
      throw new Error(res?.data?.message || 'Upload failed');
    }
    // Adjust key if your backend uses `secure_url`
    return res.data.url || res.data.secure_url;
  };


  const formik = useFormik({
    initialValues: {
      title: '',
      body: '',
      categories: [],
      imageUrl: '',
      callToActionTitle: '',
      callToActionButtonText: '',
      callToActionLink: '',
      previewBlurb: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append('createdBy', userid);
        Object.entries(values).forEach(([key, value]) => {
          if (key === 'categories') {
            value.forEach((cat, i) => {
              formData.append(`categories[${i}]`, cat);
            });
          } else {
            formData.append(key, value);
          }
        });

        if (image) {
          formData.append('headshot', image);
        }

        const result = await apipost('blog', formData, true); // true = multipart/form-data

        if (result && result.status === 201) {
          toast.success(result.data.message || 'Blog post created');
          formik.resetForm();
          setImage(null);
          handleClose();
          setUserAction(result); // callback to update list
        } else {
          toast.error(result?.data?.message || 'Error adding post');
        }
      } catch (err) {
        console.error('Blog post submission error:', err);
        toast.error('Unexpected error while submitting the blog post.');
      }
    },
  });


  const handleImageUpload = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.click();

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    try {
      // disable user typing but allow programmatic changes via API
      quill.enable(false);

      const url = await uploadViaBackend(file);

      // re-enable before we modify content
      quill.enable(true);

      // get a safe insertion point even if there is no selection/focus
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();

      // insert the image and move cursor after it
      quill.insertEmbed(index, 'image', url, 'user');
      quill.setSelection(index + 1);

      // ensure Formik stays in sync (programmatic changes may not fire onChange)
      formik.setFieldValue('body', quill.root.innerHTML, true);
      formik.setFieldTouched('body', true, false);

      toast.success('Image inserted');
    } catch (e) {
      console.error(e);
      // make sure the editor is usable again on error
      quillRef.current?.getEditor()?.enable(true);
      toast.error('Image upload failed');
    }
  };
};

  // Quill toolbar + handlers
 const quillModules = useMemo(() => ({
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
    handlers: { image: handleImageUpload },
  },
  clipboard: { matchVisual: false },
}), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'image'
  ];



  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Add Blog Post</Typography>
        <ClearIcon onClick={handleClose} sx={{ cursor: 'pointer' }} />
      </DialogTitle>

      <DialogContent dividers>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormLabel>Title</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>
 <Grid item xs={12}>
  <FormLabel>Preview Blurb</FormLabel>
  <TextField
    fullWidth
    size="small"
    multiline
    rows={3}
    name="previewBlurb"
    value={formik.values.previewBlurb}
    onChange={formik.handleChange}
    onBlur={formik.handleBlur}
    error={formik.touched.previewBlurb && Boolean(formik.errors.previewBlurb)}
    helperText={formik.touched.previewBlurb && formik.errors.previewBlurb}
  />
</Grid>

            <Grid item xs={12}>
              <FormLabel>Categories</FormLabel>
              <Select
                multiple
                fullWidth
                size="small"
                name="categories"
                value={formik.values.categories}
                onChange={formik.handleChange}
                error={formik.touched.categories && Boolean(formik.errors.categories)}
              >
                {['Industry News', 'Home Tips', 'Finance', 'Mortgage', 'Advice'].map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: 'error.main' }}>
                {formik.touched.categories && formik.errors.categories}
              </FormHelperText>
            </Grid>

           <Grid item xs={12}>
              <FormLabel>Body</FormLabel>
             <ReactQuill
  ref={quillRef}
  value={formik.values.body}
  onChange={(val) => formik.setFieldValue('body', val)}
  onBlur={() => formik.setFieldTouched('body', true)}
  modules={quillModules}
  formats={['header','bold','italic','underline','strike','list','bullet','link','image']}
  theme="snow"
/>
              {formik.touched.body && formik.errors.body && (
                <Typography sx={{ color: 'error.main' }}>{formik.errors.body}</Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Upload Image</FormLabel>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImage(e.target.files[0])}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel>Call to Action Title</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="callToActionTitle"
                value={formik.values.callToActionTitle}
                onChange={formik.handleChange}
                error={formik.touched.callToActionTitle && Boolean(formik.errors.callToActionTitle)}
                helperText={formik.touched.callToActionTitle && formik.errors.callToActionTitle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel>Call to Action Button Text</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="callToActionButtonText"
                value={formik.values.callToActionButtonText}
                onChange={formik.handleChange}
                error={formik.touched.callToActionButtonText && Boolean(formik.errors.callToActionButtonText)}
                helperText={formik.touched.callToActionButtonText && formik.errors.callToActionButtonText}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel>Call to Action Link</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="callToActionLink"
                value={formik.values.callToActionLink}
                onChange={formik.handleChange}
                error={formik.touched.callToActionLink && Boolean(formik.errors.callToActionLink)}
                helperText={formik.touched.callToActionLink && formik.errors.callToActionLink}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={formik.handleSubmit} variant="contained">
          Save
        </Button>
        <Button
          onClick={() => {
            formik.resetForm();
            setImage(null);
            handleClose();
          }}
          variant="outlined"
          color="error"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBlogPost;
